/**
 * What the Alerts screen says about each row (UI round of 2026-11-02): the
 * pure half, read by the table, the phone cards and the summary line.
 */
import type { AlertViewModel } from '../../api/adapters';
import type { SummaryFact } from '../../design-system/components/ScreenParts';
import { contactLostKind } from '../../state/attention/alert-subject';
import type { ReportCategory, ReportPrefill } from '../../state/report-problem';
import { durationWords } from '../Devices/devices-rows';

/** An alert's age in whole minutes, because this screen re-reads the clock
 *  once a minute: "3 min 40 s" would sit there for a minute, wrong. */
export function alertAgeWords(ms: number): string {
  return ms < 60_000 ? 'under a minute' : durationWords(ms - (ms % 60_000));
}

// ----------------------------------------------------------------------------
// What, and What to do.
// ----------------------------------------------------------------------------

export interface AlertSplit {
  /** The first sentence: what happened. */
  readonly what: string;
  /** Any facts between it and the instruction. */
  readonly more: string | null;
  /** The instruction, from its first sentence on. Null when the text gives
   *  none, and the row then has no box: an invented step is worse than none. */
  readonly toDo: string | null;
}

/** How an instruction opens, in the engine's and the attention list's
 *  sentences (`alert-messages.json`). A sentence the list does not know is
 *  left as a fact, which at worst reads a step as a statement. */
const INSTRUCTION_START =
  /^(If|Check|Call|Open|Arm|Cancel|Look|Go|Move|Clear|Push|Press|Wait|Tell|Find|Try|Turn|Unlock|Put|Send|Confirm|Charge|Get|Stop|Release|Scan|Ask)\b/;

/** The hotel words' own time, which the Since column already says. */
const TIME_SENTENCE = /^(Since|Reported)\b/;

/** Ends at a stop followed by a capital: leaves "L12-05" and "3.5 m" whole. */
function sentences(text: string): string[] {
  return text
    .trim()
    .split(/(?<=[.!?])\s+(?=[A-Z'"“‘(])/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function splitAlertWords(message: string): AlertSplit {
  const parts = sentences(message).filter((s) => !TIME_SENTENCE.test(s));
  if (parts.length === 0) return { what: message.trim(), more: null, toDo: null };
  const [what, ...rest] = parts as [string, ...string[]];
  const at = rest.findIndex((s) => INSTRUCTION_START.test(s));
  const facts = at < 0 ? rest : rest.slice(0, at);
  const steps = at < 0 ? [] : rest.slice(at);
  return {
    what,
    more: facts.length > 0 ? facts.join(' ') : null,
    toDo: steps.length > 0 ? steps.join(' ') : null,
  };
}

// ----------------------------------------------------------------------------
// The rule's short name.
// ----------------------------------------------------------------------------

/** One entry per rule in `alert-rules.json`, held equal by the test. */
export const ALERT_RULE_WORDS: Readonly<Record<string, string>> = {
  boarding_retry: 'robot trying the lift again',
  boarding_retry_gave_up: 'robot gave up on the lift',
  door_adapter_silent: 'door control silent',
  door_controller_offline: 'door controller offline',
  door_gate_disarmed: 'door control off',
  door_open_failed: 'door did not open',
  door_passage_stalled: 'robot stuck at a door',
  door_unknown: 'door state not known',
  door_vendor_link_down: 'door system link down',
  edge_agent_silent: 'site box health reports silent',
  edge_box_offline: 'site box silent',
  edge_gateway: 'edge box service',
  fire_mode: 'fire alarm',
  idle_behavior_diverged: 'auto charging still on',
  idle_behavior_expired: 'auto charging back on',
  lift_door_cycling: 'lift doors cycling',
  // Never "fault": a disarmed site raises this for every car on an older
  // server (`toast-words.ts::liftFaultWords`).
  lift_fault: 'lift not available',
  lift_gate_disarmed: 'lift control off',
  lift_lanes_closed: 'robots kept out of a lift',
  lift_no_reading: 'no reading from a lift',
  lift_refusal: 'lift refusing robots',
  lift_rule_not_delivered: 'lift rule not delivered',
  lift_rule_stuck: 'lift block waiting',
  robot_battery_low: 'robot battery low',
  robot_error: 'robot error',
  robot_issue: 'robot reports a problem',
  robot_stalled: 'robot stuck',
  robot_unreachable: 'robot not heard',
  task_attention: 'job not moving',
  task_failed: 'job failed',
  task_long_running: 'job running long',
};

/** "robot battery very low"; the raw rule for ops; nothing for a rule this
 *  table has not heard of, rather than a code in front of a hotel reader. */
export function alertRuleWords(
  alert: Pick<AlertViewModel, 'rule' | 'severity'>,
  audience: 'ops' | 'site',
): string | null {
  const { rule, severity } = alert;
  if (!rule) return null;
  if (audience === 'ops') return rule;
  if (rule === 'robot_battery_low' && severity === 'critical') return 'robot battery very low';
  return ALERT_RULE_WORDS[rule] ?? null;
}

// ----------------------------------------------------------------------------
// Row facts.
// ----------------------------------------------------------------------------

/** The row's opening, epoch ms: `openedAtMs`, else its `timestamp` (all
 *  digits = epoch ms, else a date string). Null when neither can be read. */
export function alertOpenedMs(alert: Pick<AlertViewModel, 'openedAtMs' | 'timestamp'>): number | null {
  const opened = alert.openedAtMs;
  if (typeof opened === 'number' && Number.isFinite(opened)) return opened;
  const ts = alert.timestamp;
  if (!ts) return null;
  const ms = /^\d+$/.test(ts) ? Number(ts) : new Date(ts).getTime();
  return Number.isFinite(ms) ? ms : null;
}

const SEVERITY_RANK: Readonly<Record<string, number>> = { critical: 0, warning: 1, info: 2 };

/** Severity, then newest: the default order. */
export function alertSeverityKey(alert: Pick<AlertViewModel, 'severity' | 'openedAtMs' | 'timestamp'>): number {
  const rank = SEVERITY_RANK[alert.severity] ?? 9;
  return rank * 1e14 - (alertOpenedMs(alert) ?? 0);
}

// ----------------------------------------------------------------------------
// The alerts a silent site box causes.
// ----------------------------------------------------------------------------

/** What a row's kind needs the box's own list to name: a door only follows
 *  the box where the box carries doors, and no site's does yet. */
const BOX_LIST_WORD = { lift: 'lifts', robot: 'robots', door: 'doors' } as const;

/** How long before the box's row a row may have opened and still be its
 *  doing: the three minutes the row waits, one 30 s sweep, and a margin. A
 *  row older than that was reported while the box was speaking. */
const BOX_LOOKBACK_MS = 4 * 60_000;

/**
 * The open rows that are only a silent site box seen from somewhere else:
 * row id -> the box row's id. Their chat send was folded under the box's
 * (`alerts_engine._held_verdict`), and they stay here, under it. A real report
 * is never one, and nor is a row opened while the box was still speaking.
 */
export function alertsUnderBox(rows: readonly AlertViewModel[]): ReadonlyMap<string, string> {
  const under = new Map<string, string>();
  const box = rows.find((a) => a.rule === 'edge_box_offline');
  const boxOpened = box ? alertOpenedMs(box) : null;
  if (!box || boxOpened === null) return under;
  for (const row of rows) {
    if (row.id === box.id) continue;
    const kind = contactLostKind(row);
    if (kind === null) continue;
    if (kind !== '' && !box.message.includes(BOX_LIST_WORD[kind])) continue;
    const opened = alertOpenedMs(row);
    if (opened !== null && opened >= boxOpened - BOX_LOOKBACK_MS) under.set(row.id, box.id);
  }
  return under;
}

/** The default order, with a row under the box straight after the box's row
 *  whatever its own severity: they are one incident to whoever is reading. */
export function alertOrderKey(
  alert: Pick<AlertViewModel, 'severity' | 'openedAtMs' | 'timestamp'>,
  box: Pick<AlertViewModel, 'severity' | 'openedAtMs' | 'timestamp'> | undefined,
): number {
  return box ? alertSeverityKey(box) + 1 : alertSeverityKey(alert);
}

/** Beside a row under the box: why it is there. */
export const UNDER_BOX_WORDS = 'Because the site box is silent';

export const SEVERITY_WORD: Readonly<Record<string, string>> = {
  critical: 'Critical',
  warning: 'Warning',
  info: 'Info',
};

export type AlertKindFilter = 'all' | 'robots' | 'lifts' | 'doors';

/** Which kind chip a row answers to. A job's alert is about its robot, and
 *  a door system's about its doors. Every other kind (the edge box, the
 *  fire alarm) shows under All only. */
export function alertMatchesKind(sourceKind: string | undefined, filter: AlertKindFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'robots') return sourceKind === 'robot' || sourceKind === 'task';
  if (filter === 'lifts') return sourceKind === 'lift';
  return sourceKind === 'door' || sourceKind === 'door_adapter';
}

/** Critical, or nobody has taken it: the box starts open. */
export function alertOpensExpanded(alert: Pick<AlertViewModel, 'severity' | 'ackedBySub'>): boolean {
  return alert.severity === 'critical' || !alert.ackedBySub;
}

/** On a phone only the most urgent starts open: a list of open cards is a
 *  long way to scroll to the next alert. */
export function alertOpensExpandedOnPhone(alert: Pick<AlertViewModel, 'severity' | 'ackedBySub'>): boolean {
  return alert.severity === 'critical' && !alert.ackedBySub;
}

// ----------------------------------------------------------------------------
// The summary line.
// ----------------------------------------------------------------------------

export interface AlertSummary {
  readonly headline: string;
  readonly facts: readonly SummaryFact[];
}

export function alertSummary(
  alerts: readonly Pick<AlertViewModel, 'severity' | 'ackedBySub' | 'openedAtMs' | 'timestamp'>[],
  mutedCount: number | null,
  nowMs: number,
): AlertSummary {
  const facts: SummaryFact[] = [];
  const add = (id: string, n: number, label: string, tone: SummaryFact['tone']) => {
    if (n > 0) facts.push({ id, label: `${n} ${label}`, tone });
  };
  const bySeverity = (s: string) => alerts.filter((a) => a.severity === s).length;
  add('critical', bySeverity('critical'), 'critical', 'bad');
  add('warning', bySeverity('warning'), bySeverity('warning') === 1 ? 'warning' : 'warnings', 'warn');
  add('info', bySeverity('info'), 'for information', 'quiet');
  const notAcked = alerts.filter((a) => !a.ackedBySub).length;
  if (notAcked > 0) facts.push({ id: 'not-acked', label: `Not acked: ${notAcked}`, tone: 'warn' });
  const opened = alerts.map(alertOpenedMs).filter((ms): ms is number => ms !== null);
  if (opened.length > 0) {
    facts.push({
      id: 'oldest',
      label: `Oldest open: ${alertAgeWords(nowMs - Math.min(...opened))}`,
      tone: 'quiet',
    });
  }
  if (mutedCount !== null && mutedCount > 0) {
    facts.push({
      id: 'muted',
      label: `${mutedCount} ${mutedCount === 1 ? 'device' : 'devices'} muted`,
      tone: 'quiet',
    });
  }
  return { headline: alerts.length === 0 ? 'Nothing open' : `${alerts.length} open`, facts };
}

// ----------------------------------------------------------------------------
// "Report a problem about this".
// ----------------------------------------------------------------------------

const REPORT_CATEGORY_OF: Readonly<Record<string, ReportCategory>> = {
  robot: 'robot',
  task: 'robot',
  lift: 'lift_door',
  door: 'lift_door',
};

/** The report form, started from this alert. A subject only for a device a
 *  person would name: a task id or a door system's id is no hotel word. */
export function reportPrefillFor(
  alert: Pick<AlertViewModel, 'id' | 'sourceKind' | 'sourceId'>,
  what: string,
): ReportPrefill {
  const named = alert.sourceKind === 'robot' || alert.sourceKind === 'lift' || alert.sourceKind === 'door';
  return {
    category: REPORT_CATEGORY_OF[alert.sourceKind ?? ''] ?? 'other',
    ...(named && alert.sourceId
      ? { subject: alert.sourceKind === 'robot' ? bareRobot(alert.sourceId) : alert.sourceId }
      : {}),
    note: `${what}\n\n`,
    alertId: alert.id,
  };
}

/** Robot rules once stamped `fleet/robot`. */
function bareRobot(id: string): string {
  return id.slice(id.lastIndexOf('/') + 1);
}
